// src/components/editors/ColorPaletteHelper.js
import React, { useState } from 'react';
import { X, ArrowLeft, Check, Sparkles } from 'lucide-react';
import { generateColorTheoryPalettes } from '../../utils/colorUtils';

/**
 * ColorPaletteHelper - Modal for generating color palettes using color theory
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 * @param {function} onSelectPalette - Callback when palette is selected
 * @param {string} currentTemplate - Current template ID (for background color)
 * @param {array} startingColors - Array of starting color options
 */
const ColorPaletteHelper = ({
  isOpen,
  onClose,
  onSelectPalette,
  currentTemplate,
  startingColors
}) => {
  const [step, setStep] = useState(1); // 1 = pick starting color, 2 = choose palette
  // eslint-disable-next-line no-unused-vars
  const [_selectedStartColor, setSelectedStartColor] = useState('#7C3AED');
  const [generatedPalettes, setGeneratedPalettes] = useState([]);

  if (!isOpen) return null;

  const handleStartColorSelect = (color) => {
    setSelectedStartColor(color);
    const palettes = generateColorTheoryPalettes(color, currentTemplate);
    setGeneratedPalettes(palettes);
    setStep(2);
  };

  const handlePaletteSelect = (palette) => {
    onSelectPalette(palette.colors);
    handleClose();
  };

  const handleClose = () => {
    setStep(1);
    setSelectedStartColor('#7C3AED');
    setGeneratedPalettes([]);
    onClose();
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setGeneratedPalettes([]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button
                onClick={handleBack}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Color Palette Helper
              </h3>
              <p className="text-sm text-gray-500">
                {step === 1 ? 'Choose a starting color' : 'Pick a color theory palette'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {step === 1 && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Start with a color that represents your practice's energy
              </p>
              <div className="grid grid-cols-3 gap-3">
                {(startingColors || []).map((colorOption, index) => {
                  // Handle both object format {color, name, vibe} and string format
                  const color = typeof colorOption === 'string' ? colorOption : colorOption?.color || '#7C3AED';
                  const name = typeof colorOption === 'string' ? `Color ${index + 1}` : colorOption?.name || 'Color';
                  const vibe = typeof colorOption === 'string' ? '' : colorOption?.vibe || '';

                  return (
                    <button
                      key={color + index}
                      onClick={() => handleStartColorSelect(color)}
                      className="group relative p-4 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition-all text-left"
                    >
                      {/* Color circle */}
                      <div
                        className="w-16 h-16 rounded-full mx-auto mb-3 shadow-md group-hover:scale-105 transition-transform"
                        style={{ backgroundColor: color }}
                      />
                      {/* Name and vibe */}
                      <div className="text-center">
                        <div className="text-sm font-semibold text-gray-900">
                          {name}
                        </div>
                        {vibe && (
                          <div className="text-xs text-gray-500 mt-1">
                            {vibe}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Based on color theory, here are harmonious palettes using your starting color
              </p>
              <div className="space-y-4">
                {generatedPalettes.map((palette) => (
                  <button
                    key={palette.id}
                    onClick={() => handlePaletteSelect(palette)}
                    className="group w-full p-4 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      {/* Color swatches */}
                      <div className="flex gap-2">
                        <div
                          className="w-12 h-12 rounded-lg shadow-sm"
                          style={{ backgroundColor: palette.colors.primary }}
                        />
                        <div
                          className="w-12 h-12 rounded-lg shadow-sm"
                          style={{ backgroundColor: palette.colors.secondary }}
                        />
                        <div
                          className="w-12 h-12 rounded-lg shadow-sm border border-gray-200"
                          style={{ backgroundColor: palette.colors.background }}
                        />
                        {palette.colors.accent && (
                          <div
                            className="w-12 h-12 rounded-lg shadow-sm"
                            style={{ backgroundColor: palette.colors.accent }}
                          />
                        )}
                      </div>

                      {/* Description */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-base font-semibold text-gray-900">
                            {palette.name}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {palette.description}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {palette.explanation}
                        </p>
                      </div>

                      {/* Arrow on hover */}
                      <div className="text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Check className="w-5 h-5" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorPaletteHelper;
